import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router-dom'
import { ArrowLeft, FilePlus, Sparkles, Wand2 } from 'lucide-react'
import { articlesApi, type CreateArticlePayload } from '@/api/articles'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { Input } from '@/components/ui/Input'
import { Label } from '@/components/ui/Label'
import { PageHeader } from '@/components/PageHeader'
import { copy } from '@/lib/copy'
import { getApiErrorMessage } from '@/lib/apiError'
import { cn } from '@/components/ui/cn'
import type { CefrLevel } from '@/types/common'

const CEFR_OPTIONS: CefrLevel[] = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2']
const DURATION_OPTIONS = [3, 5, 7, 10] as const
const MAX_TAGS = 2

export function AdminCreateArticlePage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [title, setTitle] = useState('')
  const [content, setContent] = useState('')
  const [cefrLevel, setCefrLevel] = useState<CefrLevel>('A2')
  const [estimatedTime, setEstimatedTime] = useState(5)
  const [author, setAuthor] = useState('Lexio')
  const [topic, setTopic] = useState('')
  const [imageUrl, setImageUrl] = useState('')
  const [sourceUrl, setSourceUrl] = useState('')
  const [selectedTags, setSelectedTags] = useState<string[]>([])
  const [avoidThemes, setAvoidThemes] = useState<string[]>([])
  const [published, setPublished] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [suggestNote, setSuggestNote] = useState<string | null>(null)
  const [inventoryApplied, setInventoryApplied] = useState(false)

  const tagsQuery = useQuery({
    queryKey: ['article-tags'],
    queryFn: () => articlesApi.getTags().then((response) => response.data),
    staleTime: 60_000,
  })

  const suggestMutation = useMutation({
    mutationFn: () => articlesApi.getInventorySuggestions(),
    onSuccess: (data) => {
      const nextCefr = data.suggestion.cefrLevel as CefrLevel
      setCefrLevel(CEFR_OPTIONS.includes(nextCefr) ? nextCefr : 'A2')
      setEstimatedTime(data.suggestion.estimatedTime)
      setSelectedTags(data.suggestion.tags.slice(0, MAX_TAGS))
      setAvoidThemes(data.suggestion.avoidThemes ?? [])
      setSuggestNote(data.reason)
      setInventoryApplied(true)
      setError(null)
    },
    onError: (err: unknown) => {
      setError(getApiErrorMessage(err, copy.admin.suggestFromInventoryFailed))
    },
  })

  useEffect(() => {
    if (inventoryApplied || suggestMutation.isPending || suggestMutation.isSuccess) {
      return
    }
    suggestMutation.mutate()
    // Auto-suggest once on open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const buildParams = () => ({
    cefrLevel,
    estimatedTime,
    author: author.trim() || undefined,
    topic: topic.trim() || undefined,
    tags: selectedTags,
    ...(avoidThemes.length > 0 ? { avoidThemes } : {}),
  })

  const generateMutation = useMutation({
    mutationFn: () => articlesApi.generateArticleDraft(buildParams()),
    onSuccess: (draft) => {
      setTitle(draft.title)
      setContent(draft.content)
      setError(null)
    },
    onError: (err: unknown) => {
      setError(getApiErrorMessage(err, copy.admin.generateArticleFailed))
    },
  })

  const createMutation = useMutation({
    mutationFn: (payload: CreateArticlePayload) =>
      articlesApi.createArticle(payload).then((response) => response.data),
    onSuccess: async (article) => {
      await queryClient.invalidateQueries({ queryKey: ['articles'] })
      await queryClient.invalidateQueries({ queryKey: ['article-tags'] })
      navigate(`/reading/${article.id}`)
    },
    onError: (err: unknown) => {
      setError(getApiErrorMessage(err, copy.admin.createArticleFailed))
    },
  })

  const refreshAvoidThemes = async (level: CefrLevel) => {
    try {
      const titles = await articlesApi.getRecentTitles(level)
      setAvoidThemes(titles)
    } catch {
      // Keep previous avoid list if refresh fails; generation can still proceed.
    }
  }

  const toggleTag = (tag: string) => {
    setSelectedTags((current) => {
      if (current.includes(tag)) {
        return current.filter((item) => item !== tag)
      }
      if (current.length >= MAX_TAGS) {
        return current
      }
      return [...current, tag]
    })
  }

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault()
    setError(null)

    const trimmedTitle = title.trim()
    const trimmedContent = content.trim()
    if (!trimmedTitle || !trimmedContent) {
      setError(copy.admin.createArticleValidation)
      return
    }

    createMutation.mutate({
      cefrLevel,
      estimatedTime,
      author: author.trim() || undefined,
      tags: selectedTags,
      title: trimmedTitle,
      content: trimmedContent,
      imageUrl: imageUrl.trim() || undefined,
      sourceUrl: sourceUrl.trim() || undefined,
      published,
    })
  }

  const tagOptions = tagsQuery.data ?? []

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <Link
        to="/admin"
        className="inline-flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" />
        {copy.admin.backToAdmin}
      </Link>

      <PageHeader
        title={copy.admin.createArticleTitle}
        description={copy.admin.createArticleDescription}
      />

      <Card className="p-6">
        <form className="space-y-6" onSubmit={handleSubmit} data-testid="admin-create-article-form">
          <div className="rounded-lg border border-dashed border-border bg-secondary/20 p-4">
            <p className="text-sm text-muted-foreground">{copy.admin.suggestFromInventoryHint}</p>
            {suggestNote ? (
              <p className="mt-2 text-sm text-foreground" data-testid="admin-inventory-reason">
                {suggestNote}
              </p>
            ) : null}
            <Button
              type="button"
              loading={suggestMutation.isPending}
              className="mt-3 w-auto border border-border bg-secondary text-foreground hover:bg-secondary/80"
              data-testid="admin-suggest-inventory"
              onClick={() => {
                setError(null)
                suggestMutation.mutate()
              }}
            >
              <Wand2 className="h-4 w-4" />
              {copy.admin.suggestFromInventory}
            </Button>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="article-cefr">{copy.admin.fieldCefr}</Label>
              <select
                id="article-cefr"
                value={cefrLevel}
                onChange={(event) => {
                  const next = event.target.value as CefrLevel
                  setCefrLevel(next)
                  setTitle('')
                  setContent('')
                  void refreshAvoidThemes(next)
                }}
                className="flex h-10 w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                data-testid="admin-article-cefr"
              >
                {CEFR_OPTIONS.map((level) => (
                  <option key={level} value={level}>
                    {level}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="article-time">{copy.admin.fieldEstimatedTime}</Label>
              <select
                id="article-time"
                value={estimatedTime}
                onChange={(event) => setEstimatedTime(Number(event.target.value))}
                className="flex h-10 w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                data-testid="admin-article-time"
              >
                {DURATION_OPTIONS.map((minutes) => (
                  <option key={minutes} value={minutes}>
                    {copy.admin.durationMinutes(minutes)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="article-author">{copy.admin.fieldAuthor}</Label>
            <Input
              id="article-author"
              value={author}
              onChange={(event) => setAuthor(event.target.value)}
              placeholder="Lexio"
            />
          </div>

          <div className="space-y-2">
            <Label>{copy.admin.fieldTags}</Label>
            {tagOptions.length > 0 ? (
              <div className="flex flex-wrap gap-1.5">
                {tagOptions.map((tag) => (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => toggleTag(tag)}
                    className={cn(
                      'rounded-full border px-2.5 py-0.5 text-xs font-medium capitalize transition-colors',
                      selectedTags.includes(tag)
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-border text-muted-foreground hover:border-primary/40',
                    )}
                    data-testid={`admin-article-tag-${tag}`}
                  >
                    {tag}
                  </button>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Loading tags…</p>
            )}
            <p className="text-xs text-muted-foreground">{copy.admin.fieldTagsHint}</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="article-topic">{copy.admin.fieldTopic}</Label>
            <Input
              id="article-topic"
              value={topic}
              onChange={(event) => setTopic(event.target.value)}
              placeholder="e.g. A rainy Sunday at the market"
            />
            <p className="text-xs text-muted-foreground">{copy.admin.fieldTopicHint}</p>
            {avoidThemes.length > 0 ? (
              <p className="text-xs text-muted-foreground">
                Avoiding recent themes: {avoidThemes.slice(0, 3).join(' · ')}
                {avoidThemes.length > 3 ? ` (+${avoidThemes.length - 3})` : ''}
              </p>
            ) : null}
          </div>

          <div className="rounded-lg border border-dashed border-border bg-secondary/20 p-4">
            <p className="text-sm text-muted-foreground">{copy.admin.generateArticleHint}</p>
            <Button
              type="button"
              loading={generateMutation.isPending}
              className="mt-3 w-auto border border-border bg-secondary text-foreground hover:bg-secondary/80"
              data-testid="admin-generate-article"
              onClick={() => {
                setError(null)
                generateMutation.mutate()
              }}
            >
              <Sparkles className="h-4 w-4" />
              {copy.admin.generateArticle}
            </Button>
          </div>

          <div className="space-y-2">
            <Label htmlFor="article-title">{copy.admin.fieldTitle}</Label>
            <Input
              id="article-title"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              placeholder="A day at the market"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="article-content">{copy.admin.fieldContent}</Label>
            <textarea
              id="article-content"
              value={content}
              onChange={(event) => setContent(event.target.value)}
              placeholder="Write the full article text here…"
              rows={14}
              className={cn(
                'flex w-full rounded-md border border-border bg-background px-3 py-2 text-sm shadow-sm',
                'placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              )}
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="article-image">{copy.admin.fieldImageUrl}</Label>
              <Input
                id="article-image"
                type="url"
                value={imageUrl}
                onChange={(event) => setImageUrl(event.target.value)}
                placeholder="https://…"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="article-source">{copy.admin.fieldSourceUrl}</Label>
              <Input
                id="article-source"
                type="url"
                value={sourceUrl}
                onChange={(event) => setSourceUrl(event.target.value)}
                placeholder="https://…"
              />
            </div>
          </div>

          <label className="flex cursor-pointer items-center gap-3 text-sm">
            <input
              type="checkbox"
              checked={published}
              onChange={(event) => setPublished(event.target.checked)}
              className="h-4 w-4 rounded border-border"
            />
            <span>{copy.admin.fieldPublished}</span>
          </label>

          {error ? <p className="text-sm text-destructive">{error}</p> : null}

          <Button
            type="submit"
            loading={createMutation.isPending}
            className="w-full sm:w-auto"
            data-testid="admin-create-article-submit"
          >
            <FilePlus className="h-4 w-4" />
            {copy.admin.createArticleSubmit}
          </Button>
        </form>
      </Card>
    </div>
  )
}
